import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip the /me check while the OAuth callback exchanges the session_id
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const handleAuth = (data) => {
    setToken(data.access_token);
    setUser(data.user);
    setLoading(false);
    return data.user;
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return { user: handleAuth(data) };
    } catch (e) {
      return { error: apiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return { user: handleAuth(data) };
    } catch (e) {
      return { error: apiError(e) };
    }
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
    return handleAuth(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (payload) => {
    try {
      const { data } = await api.put("/auth/me", payload);
      setUser(data);
      return { user: data };
    } catch (e) {
      return { error: apiError(e) };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, refresh, updateProfile, exchangeGoogleSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
